/**
 * The grab handle for a finger, in the ELEVATION's coordinate space.
 *
 * The plan's `grabHandleUtils` answers the same question — where does a
 * finger-sized target sit beside a selected element, so the fingertip does not
 * cover what it moves — but it answers it in WORLD METRES and draws onto a
 * canvas. The elevation draws objects as absolutely-positioned DOM boxes in
 * its own pixel space, so the geometry cannot be shared; the SIZES are, from
 * the same constants, or the two views grow different-sized buttons for the
 * same gesture.
 *
 * The offset is straight DOWN. On the plan the handle goes sideways, into the
 * room, because an element there has depth and a room to face. An elevation is
 * a flat drawing of one wall, and the one thing a bay appliance can do is
 * travel ALONG it — so an offset that shared that axis would sit under the
 * finger's own travel. Below the object is clear of the drag, clear of the
 * uppers, and next to where a hand already rests.
 */
import {
  GRAB_HANDLE_RADIUS_PX,
  GRAB_HANDLE_HIT_RADIUS_PX,
  GRAB_HANDLE_GAP_PX,
} from './grabHandleUtils';

export { GRAB_HANDLE_RADIUS_PX, GRAB_HANDLE_HIT_RADIUS_PX, GRAB_HANDLE_GAP_PX };

/**
 * Where the handle sits, in the same pre-zoom pixel space the object divs are
 * laid out in (`x2D` / `y2D`).
 *
 * Screen-sized constants are divided by `zoom` — the host's CSS transform
 * scales this whole space, so a handle that did not divide would swell with
 * the drawing and stop being a finger target at either end of the range.
 *
 * @param {{x2D: number, y2D: number, drawnHeightPx: number}} box
 * @param {number} zoom  the host's zoom (`zoomScale`), > 0.
 * @returns {{x: number, y: number, radius: number, hitRadius: number}|null}
 */
export function elevationHandleGeometry(box, zoom = 1) {
  const { x2D, y2D, drawnHeightPx } = box || {};
  if (![x2D, y2D, drawnHeightPx].every(Number.isFinite)) return null;
  if (!(zoom > 0)) return null;
  const radius = GRAB_HANDLE_RADIUS_PX / zoom;
  const gap = GRAB_HANDLE_GAP_PX / zoom;
  return {
    x: x2D,
    y: y2D + drawnHeightPx / 2 + gap + radius,
    radius,
    hitRadius: GRAB_HANDLE_HIT_RADIUS_PX / zoom,
  };
}

/**
 * Did a press land on the handle? `point` is in the same pre-zoom space.
 *
 * The hit radius is larger than the visual one — Apple's 44 pt minimum as a
 * radius — so the ring around the disc is live, exactly as on the plan.
 */
export function hitTestElevationHandle(point, box, zoom = 1) {
  const g = elevationHandleGeometry(box, zoom);
  if (!g || !point) return false;
  const dx = point.x - g.x;
  const dy = point.y - g.y;
  return dx * dx + dy * dy <= g.hitRadius * g.hitRadius;
}

/**
 * A two-way arrow for a handle disc, centred at `c` — along the wall
 * (`'horizontal'`, the run and bay handles) or up and down (`'vertical'`, a
 * backsplash band's top edge, call item #28).
 *
 * The plan's `drawHandleDisc` draws the same glyph with canvas strokes, its
 * arrow turned to the axis the handle moves on; this is the SVG twin, which
 * is why it takes the same two proportions (arm and head as fractions of the
 * radius) rather than absolute sizes. The centre is given as x and y
 * separately: a run handle's hit box is deliberately not square (its reach is
 * clipped on the side facing its neighbour), so the glyph cannot assume the
 * two agree.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} arm from the centre to each tip
 * @param {number} head each arrowhead's half-width
 * @param {'horizontal'|'vertical'} [axis]
 * @returns {string} an SVG path
 */
export const arrowPath = (cx, cy, arm, head, axis = 'horizontal') =>
  [-1, 1]
    .map((dir) => {
      if (axis === 'vertical') {
        const tip = cy + dir * arm;
        return [
          `M ${cx} ${cy}`,
          `L ${cx} ${tip}`,
          `M ${cx - head} ${tip - dir * head}`,
          `L ${cx} ${tip}`,
          `L ${cx + head} ${tip - dir * head}`,
        ].join(' ');
      }
      const tip = cx + dir * arm;
      return [
        `M ${cx} ${cy}`,
        `L ${tip} ${cy}`,
        `M ${tip - dir * head} ${cy - head}`,
        `L ${tip} ${cy}`,
        `L ${tip - dir * head} ${cy + head}`,
      ].join(' ');
    })
    .join(' ');
